// frontend/src/components/AddressMapOverlay.jsx
import React, { useEffect, useRef, useState } from "react";

const LINE_COLORS = {
  "1호선": "#0052A4",
  "2호선": "#00A84D",
  "3호선": "#EF7C1C",
  "4호선": "#00A5DE",
  "5호선": "#996CAC",
  "6호선": "#CD7C2F",
  "7호선": "#747F00",
  "8호선": "#E6186C",
  "9호선": "#BDB092",
  "경의중앙선": "#77C4A3",
  "수인분당선": "#F5A200",
  "신분당선": "#D4003B",
  // 필요하면 계속 추가
};

export default function AddressMapOverlay({
  open,
  onClose,
  address,
  lat,
  lng,
  estateNo,
  onSelectStation,   // ★ 지도 결과를 에디터로 올려보내는 콜백
}) {
  const mapRef = useRef(null);
  const mapContainerRef = useRef(null);
  const [position, setPosition] = useState(null); // {lat,lng}
  const [exits, setExits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [geoError, setGeoError] = useState("");

  // 오버레이 열릴 때 좌표/주소 기준 좌표 잡기
  useEffect(() => {
    if (!open) return;

    setExits([]);
    setGeoError("");
    setPosition(null);

    const kakao = window.kakao;

    // 이미 lat,lng 있으면 그대로 사용
    if (lat && lng) {
      setPosition({ lat, lng });
      return;
    }

    // 주소로 지오코딩
    if (!address || !kakao || !kakao.maps || !kakao.maps.services) {
      setGeoError("주소 또는 카카오맵 객체가 없습니다.");
      return;
    }

    const geocoder = new kakao.maps.services.Geocoder();
    geocoder.addressSearch(address, (result, status) => {
      if (status === kakao.maps.services.Status.OK && result[0]) {
        const r = result[0];
        setPosition({
          lat: parseFloat(r.y),
          lng: parseFloat(r.x),
        });
      } else {
        setGeoError("주소로 위치를 찾을 수 없습니다.");
      }
    });
  }, [open, address, lat, lng]);

  // position이 결정되면 지도 생성 + 최근접 지하철 호출
  useEffect(() => {
    if (!open || !position) return;

    const kakao = window.kakao;
    if (!kakao || !kakao.maps) return;

    const options = {
      center: new kakao.maps.LatLng(position.lat, position.lng),
      level: 4,
    };

    const map = new kakao.maps.Map(mapContainerRef.current, options);
    mapRef.current = map;

    // 기준 위치 마커
    const marker = new kakao.maps.Marker({
      position: new kakao.maps.LatLng(position.lat, position.lng),
    });
    marker.setMap(map);

    // 근처 지하철 출구 요청
    setLoading(true);
    fetch(
      `/api/subway/nearest?lat=${position.lat}&lng=${position.lng}`
    )
      .then((res) => res.json())
      .then((data) => {
        if (!data.ok) throw new Error("api error");
        const exits = data.exits || [];
        setExits(exits);

        // ★ 에디터로 역명/도보시간 전달 → station, station_distance 자동 채우기용
        if (exits.length > 0 && typeof onSelectStation === "function") {
          const top = exits[0];
          onSelectStation({
            // 역명: "역" 붙여서 보냄
            station: top.station_name ? `${top.station_name}역` : "",
            // 도보시간(분)
            station_distance:
              top.walk_min !== undefined && top.walk_min !== null
                ? top.walk_min
                : null,
            // 교통수단 기본값: 도보
            station_transport: "도보",
            // 혹시 다른 이름으로도 받을 수 있게 여분 키도 같이 보냄
            distanceMinutes: top.walk_min ?? null,
            transport: "도보",
          });
        }

        // ★ estateNo가 있으면 가장 가까운 1개를 estate 테이블에 저장
        if (estateNo && exits.length > 0) {
          const top = exits[0];
          fetch(`/api/subway/save-nearest/${estateNo}`, {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(top),
          }).catch((e) => {
            console.error("save-nearest error", e);
          });
        }
      })
      .catch((err) => {
        console.error(err);
        setExits([]);
      })
      .finally(() => setLoading(false));
  }, [open, position]);

  const nearest = exits[0] || null;

  if (!open) return null;

  return (
    <div
      className="map-overlay-backdrop"
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.5)",
        zIndex: 2000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
      onClick={onClose}
    >
      <div
        className="map-overlay-panel"
        style={{
          width: "40%",
          maxWidth: "400px",
          height: "30%",
          background: "#fff",
          borderRadius: "8px",
          padding: "12px",
          display: "flex",
          flexDirection: "column",
        }}
        onClick={(e) => e.stopPropagation()} // 내부 클릭 시 닫힘 방지
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "8px",
          }}
        >
          <div>
            <strong>지도 확인</strong>
            <div style={{ fontSize: "12px", color: "#555" }}>{address}</div>
          </div>
          <button type="button" onClick={onClose}>
            닫기
          </button>
        </div>

        <div
          ref={mapContainerRef}
          style={{ flex: 1, border: "1px solid #ddd", marginBottom: "8px" }}
        />

        {geoError && (
          <div style={{ color: "red", marginBottom: "4px" }}>{geoError}</div>
        )}

      {/* ===== 가까운 역 1개 + 환승 시 호선별 박스 반복 ===== */}
      <div
        style={{
          maxHeight: "150px",
          overflowY: "auto",
          borderTop: "1px solid #eee",
          paddingTop: "4px",
        }}
      >
        {loading && <div>가까운 지하철을 조회 중입니다...</div>}

        {!loading && (!exits || exits.length === 0) && (
          <div>주변에 지하철역 정보가 없습니다.</div>
        )}

        {!loading && exits && exits.length > 0 && (
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            {(exits[0].lines && exits[0].lines.length > 0
              ? exits[0].lines
              : [null]
            ).map((line, idx) => {
              const bg =
                line && LINE_COLORS[line]
                  ? LINE_COLORS[line]
                  : "rgba(0,0,0,0.7)";

              return (
                <div
                  key={`${exits[0].station_name}-${line || "default"}-${idx}`}
                  style={{
                    padding: "6px 10px",
                    borderRadius: 6,
                    background: bg,
                    color: "#fff",
                    fontSize: 13,
                  }}
                >
                  <div style={{ fontWeight: 600 }}>
                    {exits[0].station_name}역
                  </div>
                  <div style={{ fontSize: 12 }}>
                    도보 약 {exits[0].walk_min}분 ({exits[0].distance_m}m)
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      </div>
    </div>
  );
}